/** Interval partitioning: touching [start,end) intervals can share one lane. */
export function layoutIntervalLanes(items) {
    const laneEnds = [];
    const placed = items.map((item, index) => ({ item, index }))
        .sort((a, b) => a.item.startMs - b.item.startMs || a.item.endMs - b.item.endMs || a.index - b.index)
        .map(({ item }) => {
        let lane = laneEnds.findIndex((end) => end <= item.startMs);
        if (lane === -1)
            lane = laneEnds.length;
        laneEnds[lane] = item.endMs;
        return { item, lane };
    });
    return { items: placed, laneCount: Math.max(1, laneEnds.length) };
}
export function timelineScale(value) {
    return Number.isFinite(value) && value > 0 ? value : 80;
}
export function formatTimelineTime(timeMs, milliseconds = false) {
    const value = Math.max(0, Number.isFinite(timeMs) ? Math.round(timeMs) : 0);
    const minutes = Math.floor(value / 60_000);
    const seconds = Math.floor(value / 1000) % 60;
    const fraction = milliseconds ? `.${String(value % 1000).padStart(3, "0")}` : "";
    return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}${fraction}`;
}
/** Choose readable 1/2/5 ticks and bound generated DOM even for extreme view inputs. */
export function createTimelineTicks(durationMs, pxPerSecond) {
    const duration = Math.max(0, Number.isFinite(durationMs) ? durationMs : 0);
    const minimumStep = Math.max(70_000 / timelineScale(pxPerSecond), duration / 400, 1);
    const power = 10 ** Math.floor(Math.log10(minimumStep));
    const step = [1, 2, 5, 10].map((n) => n * power).find((n) => n >= minimumStep) ?? power * 10;
    return Array.from({ length: Math.floor(duration / step) + 1 }, (_, index) => index * step);
}
/** Compute every draft from the gesture's original positions, so backtracking is reversible. */
export function calculateClipGesture(clip, kind, deltaMs, snapPoints, thresholdMs, disableSnap, clips = []) {
    const original = { clipId: clip.id, kind, startMs: clip.startMs, endMs: clip.endMs };
    if (deltaMs === 0)
        return { change: original, snapMs: null };
    const ordered = [...clips.filter((item) => item.id !== clip.id), clip]
        .sort((a, b) => a.startMs - b.startMs || a.endMs - b.endMs || a.id.localeCompare(b.id));
    const index = ordered.findIndex((item) => item.id === clip.id);
    const previous = ordered[index - 1];
    const next = ordered[index + 1];
    const duration = clip.endMs - clip.startMs;
    if (kind === "move") {
        let target = index;
        // The moving leading edge must pass the ORIGINAL neighbor midpoint. Snapping
        // does not decide order, and Alt does not disable the collision boundary.
        if (deltaMs > 0) {
            while (target + 1 < ordered.length) {
                const neighbor = ordered[target + 1];
                if (clip.endMs + deltaMs <= (neighbor.startMs + neighbor.endMs) / 2)
                    break;
                target++;
            }
        }
        else {
            while (target > 0) {
                const neighbor = ordered[target - 1];
                if (clip.startMs + deltaMs >= (neighbor.startMs + neighbor.endMs) / 2)
                    break;
                target--;
            }
        }
        if (target !== index) {
            const block = ordered.slice(Math.min(index, target), Math.max(index, target) + 1);
            const gaps = block.slice(1).map((item, slot) => item.startMs - block[slot].endMs);
            // Old overlapping timelines still render in lanes; don't manufacture an
            // invalid permutation from their negative gaps.
            if (gaps.every((gap) => gap >= 0)) {
                const rotated = target > index ? [...block.slice(1), clip] : [clip, ...block.slice(0, -1)];
                let position = block[0].startMs;
                const reorderedClips = rotated.map((item, slot) => {
                    const startMs = position;
                    const endMs = startMs + item.endMs - item.startMs;
                    position = endMs + (gaps[slot] ?? 0);
                    return { clipId: item.id, startMs, endMs };
                });
                const moved = reorderedClips.find((item) => item.clipId === clip.id);
                return { change: { ...original, ...moved, reorderedClips }, snapMs: null };
            }
        }
    }
    const minimumStart = Math.max(0, previous?.endMs ?? 0);
    const maximumEnd = next?.startMs ?? Infinity;
    let startMs = clip.startMs + (kind === "trim-end" ? 0 : deltaMs);
    let endMs = clip.endMs + (kind === "trim-start" ? 0 : deltaMs);
    let snapMs = null;
    if (kind === "move") {
        const constrained = Math.max(minimumStart, Math.min(maximumEnd - duration, startMs));
        if (constrained !== startMs)
            snapMs = startMs < minimumStart ? minimumStart : maximumEnd;
        startMs = constrained;
        endMs = startMs + duration;
    }
    else if (kind === "trim-start") {
        const constrained = Math.max(minimumStart, Math.min(clip.endMs - 1, startMs));
        if (constrained !== startMs)
            snapMs = constrained;
        startMs = constrained;
    }
    else {
        const constrained = Math.max(clip.startMs + 1, Math.min(maximumEnd, endMs));
        if (constrained !== endMs)
            snapMs = constrained;
        endMs = constrained;
    }
    let adjustment = 0;
    let bestDistance = Infinity;
    // A hard boundary takes precedence over cue/clip soft snapping.
    if (!disableSnap && snapMs === null) {
        const edges = kind === "move" ? [startMs, endMs] : [kind === "trim-start" ? startMs : endMs];
        for (const point of snapPoints) {
            for (const edge of edges) {
                const candidateAdjustment = point - edge;
                const candidateStart = startMs + (kind === "trim-end" ? 0 : candidateAdjustment);
                const candidateEnd = endMs + (kind === "trim-start" ? 0 : candidateAdjustment);
                if (candidateStart < minimumStart || candidateEnd > maximumEnd || candidateEnd <= candidateStart)
                    continue;
                const distance = Math.abs(point - edge);
                if (distance <= thresholdMs && distance < bestDistance) {
                    bestDistance = distance;
                    adjustment = candidateAdjustment;
                    snapMs = point;
                }
            }
        }
    }
    if (kind !== "trim-end")
        startMs += adjustment;
    if (kind !== "trim-start")
        endMs += adjustment;
    return { change: { ...original, startMs: Math.round(startMs), endMs: Math.round(endMs) }, snapMs };
}
export function basicDraftError(change) {
    if (!Number.isFinite(change.startMs) || !Number.isFinite(change.endMs))
        return "请输入有效时间";
    if (change.startMs < 0)
        return "开始时间不能早于 0 秒";
    if (change.endMs <= change.startMs)
        return "结束时间应晚于开始时间";
    return undefined;
}
